import React, { useMemo, useRef, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { GET_SADDLE_DETAIL_QUERY } from '../../apollo/queries/saddleDetails'
import { UPDATE_SADDLE_VARIANT } from '../../apollo/mutations/updateSaddleVariant'
import { UPDATE_SADDLE } from '../../apollo/mutations/updateSaddle'
import { BeatLoader } from 'react-spinners'
import { FaArrowLeft, FaEdit } from 'react-icons/fa'
import { formatString } from '../../utils'
import { useUserContext } from '../../context/UserContext.jsx'
import useSignedUrlUpload from '../../hooks/useSignedUrlUpload'
import { createToast } from '../../utils/Toast'
import Button from '../../components/atoms/Buttons'
import ModalComponent from '../../components/atoms/Modal/ModalComponent/ModalComponent'

const Toast = createToast()

const VALID_MESH_EXT = ['obj', 'stl', 'mesh']

function formatUsdDisplay(raw) {
  if (raw === '' || raw == null) return ''
  const n = parseFloat(String(raw).replace(/[^0-9.]/g, ''))
  if (Number.isNaN(n)) return ''
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n)
}

function sanitizePriceInput(s) {
  let t = String(s).replace(/[^0-9.]/g, '')
  const firstDot = t.indexOf('.')
  if (firstDot !== -1) {
    const rest = t.slice(firstDot + 1).replace(/\./g, '')
    t = t.slice(0, firstDot + 1) + rest
  }
  return t
}

function SaddleView() {
  const { encodedId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { userData } = useUserContext()
  const effectiveBrandName = useMemo(() => {
    const fromState = location.state?.brandName
    if (fromState) return fromState
    const b = userData?.brand?.toLowerCase?.()
    if (b === 'cactus') return 'cactussaddlery'
    if (b && b !== 'saddlefitadmin') return userData.brand
    return null
  }, [location.state?.brandName, userData?.brand])
  const saddleId = encodedId ? decodeURIComponent(encodedId) : null

  const { data, loading, refetch } = useQuery(GET_SADDLE_DETAIL_QUERY, {
    variables: { id: saddleId, brandName: effectiveBrandName ?? null },
    skip: !saddleId,
    fetchPolicy: 'network-only',
  })

  const [updateSaddleVariant, { loading: variantSaving }] =
    useMutation(UPDATE_SADDLE_VARIANT)
  const [updateSaddle, { loading: saddleSaving }] = useMutation(UPDATE_SADDLE)
  const { uploadFile } = useSignedUrlUpload()

  const [editingVariant, setEditingVariant] = useState(null)
  const [variantForm, setVariantForm] = useState({
    size: '',
    price: '',
    quantity: '',
  })
  const [priceFocused, setPriceFocused] = useState(false)
  const [meshUploading, setMeshUploading] = useState(false)
  const meshInputRef = useRef(null)

  const [discountOpen, setDiscountOpen] = useState(false)
  const [discountForm, setDiscountForm] = useState({
    mode: 'amount',
    value: '',
  })

  // Dummy state setters used to satisfy ModalComponent's API
  const [, setCancelModal] = useState(false)

  if (loading) {
    return (
      <div className="loader-container !w-[260px] h-full flex justify-center items-center">
        <BeatLoader color={'#2B364B'} size={20} />
      </div>
    )
  }

  const items = data?.listSaddleDetail || []
  if (!items || items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center">
        <p className="font-montserrat text-xl text-gray-500 font-semibold">
          No saddle details found.
        </p>
      </div>
    )
  }

  const main =
    items.find(i => i.type === 'PRODUCT') ||
    items.find(i => i.sk && !String(i.sk).includes('#variant#')) ||
    items[0]

  const variants = items.filter(
    v => v?.type === 'VARIANT' || (v?.sk && String(v.sk).includes('#variant#')),
  )

  const openEdit = v => {
    setEditingVariant(v)
    setPriceFocused(false)
    setVariantForm({
      size: v.size ?? '',
      price:
        v.price != null && v.price !== ''
          ? String(v.price).replace(/[^0-9.]/g, '')
          : '',
      quantity: v.quantity ?? '',
    })
  }

  const closeEdit = () => {
    setEditingVariant(null)
    setPriceFocused(false)
  }

  const saveVariant = async () => {
    if (!editingVariant) return
    const sizeNum = parseFloat(variantForm.size)
    const priceNum = parseFloat(variantForm.price)
    const qtyNum = parseInt(variantForm.quantity, 10)
    if (
      Number.isNaN(sizeNum) ||
      Number.isNaN(priceNum) ||
      Number.isNaN(qtyNum)
    ) {
      Toast.fire({
        icon: 'error',
        title: 'Size, price and quantity are required',
      })
      return
    }
    try {
      await updateSaddleVariant({
        variables: {
          input: {
            pk: editingVariant.pk,
            sk: editingVariant.sk,
            size: sizeNum,
            price: priceNum,
            quantity: qtyNum,
          },
        },
      })
      Toast.fire({ icon: 'success', title: 'Saddle Variant Updated Successfully!' })
      closeEdit()
      refetch()
    } catch (err) {
      Toast.fire({ icon: 'error', title: err?.message || 'Update failed' })
    }
  }

  const openDiscount = () => {
    setDiscountForm({ mode: 'amount', value: main.discount ?? '' })
    setDiscountOpen(true)
  }

  const closeDiscount = () => setDiscountOpen(false)

  const saveDiscount = async () => {
    const num = parseFloat(discountForm.value)
    if (Number.isNaN(num) || num < 0) {
      Toast.fire({ icon: 'error', title: 'Enter a valid discount' })
      return
    }
    if (discountForm.mode === 'percent' && num > 100) {
      Toast.fire({ icon: 'error', title: 'Percentage must be 0-100' })
      return
    }
    const basePrice = parseFloat(main.price) || 0
    const finalDiscount =
      discountForm.mode === 'percent'
        ? Math.round(basePrice * (num / 100) * 100) / 100
        : num
    try {
      const brandForUpdate = main.brandName ?? main.pk ?? effectiveBrandName
      await updateSaddle({
        variables: {
          input: {
            pk: main.pk,
            sk: main.sk,
            brandName: brandForUpdate,
            discount: finalDiscount,
          },
        },
      })
      Toast.fire({ icon: 'success', title: 'Discount updated' })
      closeDiscount()
      refetch()
    } catch (err) {
      Toast.fire({ icon: 'error', title: err?.message || 'Update failed' })
    }
  }

  const triggerMeshPicker = () => {
    if (meshUploading || saddleSaving) return
    meshInputRef.current?.click()
  }

  const handleMeshFile = async e => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const ext = file.name.split('.').pop()?.toLowerCase()
    if (!VALID_MESH_EXT.includes(ext)) {
      Toast.fire({
        icon: 'error',
        title: 'Invalid 3D object file. Use .obj, .stl, or .mesh',
      })
      return
    }
    setMeshUploading(true)
    try {
      const url = await uploadFile(file, 'mesh', main.sk)
      const brandForUpdate = main.brandName ?? main.pk ?? effectiveBrandName
      await updateSaddle({
        variables: {
          input: {
            pk: main.pk,
            sk: main.sk,
            brandName: brandForUpdate,
            status: main.status,
            price: main.price,
            productTitle: main.productTitle ?? '',
            discipline: main.discipline ?? '',
            description: main.description ?? '',
            discount: main.discount ?? 0,
            sku: main.saddlefitSku ?? '',
            gallery: main.gallery ?? [],
            photo: main.photo,
            scannedFile: url,
            gulletWidth: main.measurements?.gulletWidth,
            barAngle: main.measurements?.barAngle,
          },
        },
      })
      Toast.fire({ icon: 'success', title: '3D object file uploaded' })
      refetch()
    } catch (err) {
      Toast.fire({ icon: 'error', title: err?.message || 'Upload failed' })
    } finally {
      setMeshUploading(false)
    }
  }

  const hasMesh = main.scannedFiles && main.scannedFiles.length > 0
  const meshBusy = meshUploading || saddleSaving

  const inputClass =
    'mt-2 block w-full box-border h-[48px] border border-gray-300 rounded-lg px-4 text-sm font-montserrat text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2B364B] focus:border-transparent no-spinner'
  const labelClass =
    'block text-sm font-montserrat text-gray-700 font-medium'

  return (
    <div className="w-full max-w-5xl mx-auto bg-white p-6 rounded-lg shadow-sm font-montserrat">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
        <div className="w-full">
          <div className="flex items-center gap-3">
            <FaArrowLeft
              onClick={() => navigate(-1)}
              className="text-gray-500 hover:text-gray-700 cursor-pointer"
              size={16}
              aria-label="Back"
              title="Back"
            />
            <h2 className="text-2xl font-barlowcondensed font-bold">
              {main.productTitle || 'Untitled'}
            </h2>
          </div>
          {main.photo && (
            <div className="w-[160px] bg-gray-100 rounded overflow-hidden border flex-shrink-0 mb-6">
              <img
                src={main.photo}
                alt="Saddle thumbnail"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <p className="text-sm text-gray-500 mt-1">
            Brand:{' '}
            <span className="font-medium text-gray-700">
              {formatString(main.brandName)}
            </span>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Status:{' '}
            <span className="font-medium text-gray-600">
              {formatString(main.status) || 'N/A'}
            </span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left content column */}
        <div className="col-span-2">
          {/* Description */}
          <div className="mb-6">
            <h3 className="text-base text-gray-600 mb-2 font-montserrat">
              Description
            </h3>
            <div
              className="prose max-w-none text-sm text-gray-800"
              dangerouslySetInnerHTML={{
                __html: main.description || '<em>—</em>',
              }}
            />
          </div>

          {/* Gallery */}
          <div className="mb-6">
            <h3 className="text-base text-gray-600 mb-2 font-montserrat">
              Gallery
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(main.gallery || []).map((src, i) => (
                <div
                  key={i}
                  className="h-32 bg-gray-100 rounded overflow-hidden border"
                >
                  <img
                    src={src}
                    alt={`gallery-${i}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              {!(main.gallery || []).length && (
                <div className="text-gray-400 text-sm">No images</div>
              )}
            </div>
          </div>

          {/* 3D object file (e.g. .obj / .stl) */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base text-gray-600 font-montserrat">
                3D object file
              </h3>
              <Button
                primary
                type="button"
                onClick={triggerMeshPicker}
                loading={meshBusy}
                disabled={meshBusy}
                className="create-saddle-btn !w-[160px] !h-auto !text-white !rounded-md"
              >
                {hasMesh ? 'Replace' : 'Upload 3D object'}
              </Button>
              <input
                ref={meshInputRef}
                type="file"
                accept=".obj,.stl,.mesh"
                onChange={handleMeshFile}
                className="hidden"
              />
            </div>
            {hasMesh ? (
              <a
                href={main.scannedFiles[0]}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline font-medium"
              >
                {decodeURIComponent(
                  main.scannedFiles[0].split('/').pop() || '3d-object',
                )}
              </a>
            ) : (
              <div className="text-sm text-gray-400">No 3D object file</div>
            )}
          </div>
        </div>

        {/* Right side column */}
        <aside className="col-span-1">
          {/* Details card */}
          <div className="mb-4 p-4 border border-gray-100 rounded-lg shadow-sm">
            <h4 className="text-base text-gray-600 mb-3 font-montserrat">
              Details
            </h4>
            <dl className="text-sm text-gray-800 space-y-2">
              <div className="flex justify-between">
                <dt className="text-gray-500">Price</dt>
                <dd className="font-medium">${main.price ?? 'N/A'}</dd>
              </div>
              <div className="flex justify-between items-center">
                <dt className="text-gray-500">Discount</dt>
                <dd className="font-medium flex items-center gap-2">
                  <span>${main.discount ?? 0}</span>
                  <FaEdit
                    className="cursor-pointer text-[#2B364B]"
                    onClick={openDiscount}
                    title="Edit discount"
                  />
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Condition</dt>
                <dd className="font-medium">
                  {formatString(main.newOrUsed) || 'N/A'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Discipline</dt>
                <dd className="font-medium">
                  {formatString(main.discipline) || 'N/A'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">Status</dt>
                <dd className="font-medium">
                  {formatString(main.status) || 'N/A'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-gray-500">SKU</dt>
                <dd className="font-medium">{main.saddlefitSku || 'N/A'}</dd>
              </div>
            </dl>
          </div>

          {/* Measurements card */}
          <div className="mb-4 p-4 border border-gray-100 rounded-lg shadow-sm">
            <h4 className="text-base text-gray-600 mb-3 font-montserrat">
              Measurements
            </h4>
            <div className="text-sm text-gray-800 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Bar Angle</span>
                <span className="font-medium">
                  {main.measurements?.barAngle ?? 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Gullet Width</span>
                <span className="font-medium">
                  {main.measurements?.gulletWidth ?? 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Gullet Height</span>
                <span className="font-medium">
                  {main.measurements?.gulletHeight ?? 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Variants card */}
          <div className="p-4 border border-gray-100 rounded-lg shadow-sm">
            <h4 className="text-base text-gray-600 mb-3 font-montserrat">
              Variants
            </h4>
            <div className="space-y-2 text-sm">
              {variants.map((v, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center bg-gray-50 p-2 rounded"
                >
                  <div>
                    <div className="font-medium">Size: {v.size || '-'}</div>
                    <div className="text-gray-500">
                      Qty: {v.quantity ?? '-'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">${v.price ?? '-'}</div>
                    <div className="text-gray-500">
                      SKU: {v.saddlefitSku || '-'}
                    </div>
                  </div>
                  <FaEdit
                    className="ml-2 cursor-pointer text-[#2B364B]"
                    onClick={() => openEdit(v)}
                    title="Edit variant"
                  />
                </div>
              ))}
              {variants.length === 0 && (
                <div className="text-sm text-gray-400">No variants</div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Discount edit modal */}
      {discountOpen && (
        <ModalComponent
          title="Edit Discount"
          openEditModal={true}
          setOpenModal={closeDiscount}
          setOpenEditModal={closeDiscount}
          setCancelModal={setCancelModal}
          updateLoading={saddleSaving}
          onUpdate={saveDiscount}
        >
          <form
            id="discount-form"
            onSubmit={e => {
              e.preventDefault()
              saveDiscount()
            }}
            className="px-6 pt-6 w-full"
          >
            <div className="space-y-5">
              <div className="flex gap-6 text-sm font-montserrat text-gray-700">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="discount-mode"
                    value="amount"
                    checked={discountForm.mode === 'amount'}
                    onChange={() =>
                      setDiscountForm(f => ({ ...f, mode: 'amount' }))
                    }
                    className="accent-[#2B364B] w-4 h-4"
                  />
                  Amount ($)
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="discount-mode"
                    value="percent"
                    checked={discountForm.mode === 'percent'}
                    onChange={() =>
                      setDiscountForm(f => ({ ...f, mode: 'percent' }))
                    }
                    className="accent-[#2B364B] w-4 h-4"
                  />
                  Percentage (%)
                </label>
              </div>
              <div>
                <label className={labelClass}>
                  {discountForm.mode === 'percent'
                    ? 'Percent off'
                    : 'Discount amount'}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={discountForm.mode === 'percent' ? '100' : undefined}
                  value={discountForm.value}
                  placeholder={
                    discountForm.mode === 'percent' ? 'e.g. 10' : 'e.g. 250'
                  }
                  onChange={e =>
                    setDiscountForm(f => ({ ...f, value: e.target.value }))
                  }
                  className={inputClass}
                />
              </div>
              {discountForm.mode === 'percent' && main.price ? (
                <p className="text-xs text-gray-500 font-montserrat">
                  ≈ $
                  {(
                    Math.round(
                      parseFloat(main.price) *
                        ((parseFloat(discountForm.value) || 0) / 100) *
                        100,
                    ) / 100
                  ).toFixed(2)}{' '}
                  off ${main.price}
                </p>
              ) : null}
            </div>
          </form>
        </ModalComponent>
      )}

      {/* Variant edit modal */}
      {editingVariant && (
        <ModalComponent
          title="Update Variant"
          openEditModal={true}
          setOpenModal={closeEdit}
          setOpenEditModal={closeEdit}
          setCancelModal={setCancelModal}
          updateLoading={variantSaving}
          onUpdate={saveVariant}
        >
          <form
            id="variant-edit-form"
            onSubmit={e => {
              e.preventDefault()
              saveVariant()
            }}
            className="box-border w-full min-w-0 px-6 sm:px-8 pt-4 pb-1"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-x-5 sm:gap-y-5">
              <div className="min-w-0">
                <label className={labelClass} htmlFor="variant-seat-size">
                  Seat Size
                </label>
                <input
                  id="variant-seat-size"
                  type="number"
                  step="0.5"
                  placeholder="Seat size"
                  value={variantForm.size}
                  onChange={e =>
                    setVariantForm(f => ({ ...f, size: e.target.value }))
                  }
                  className={inputClass}
                />
              </div>
              <div className="min-w-0">
                <label className={labelClass} htmlFor="variant-price">
                  Price
                </label>
                <input
                  id="variant-price"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="Price"
                  value={
                    priceFocused
                      ? variantForm.price
                      : variantForm.price === ''
                        ? ''
                        : formatUsdDisplay(variantForm.price)
                  }
                  onFocus={() => setPriceFocused(true)}
                  onBlur={() => setPriceFocused(false)}
                  onChange={e =>
                    setVariantForm(f => ({
                      ...f,
                      price: sanitizePriceInput(e.target.value),
                    }))
                  }
                  className={inputClass}
                />
              </div>
              <div className="min-w-0 sm:col-span-2">
                <label className={labelClass} htmlFor="variant-qty">
                  Quantity
                </label>
                <input
                  id="variant-qty"
                  type="number"
                  min="0"
                  placeholder="Quantity"
                  value={variantForm.quantity}
                  onChange={e =>
                    setVariantForm(f => ({ ...f, quantity: e.target.value }))
                  }
                  className={inputClass}
                />
              </div>
            </div>
          </form>
        </ModalComponent>
      )}
    </div>
  )
}

export default SaddleView
